import type {
  IssueWorkflowOutDto,
  WorkflowStageOutDto,
} from 'shared/domain/workflow/types';
import { sortByFractionalOrder } from 'helpers/sort';
import { normalizeEntityIdentifier } from 'shared/utils/id/normalizeEntityIdentifier';

/** Compares workflow-related ids regardless of plain string vs Mongo $oid wrapper. */
export function areWorkflowEntityIdsEqual(
  left: unknown,
  right: unknown
): boolean {
  const normalizedLeft = normalizeEntityIdentifier(left);
  const normalizedRight = normalizeEntityIdentifier(right);
  return (
    normalizedLeft !== undefined &&
    normalizedRight !== undefined &&
    normalizedLeft === normalizedRight
  );
}

export function findWorkflowById(
  workflows: IssueWorkflowOutDto[],
  workflowId: string
): IssueWorkflowOutDto | undefined {
  return workflows.find((workflow) =>
    areWorkflowEntityIdsEqual(workflow._id, workflowId)
  );
}

export function findStageById(
  workflows: IssueWorkflowOutDto[],
  stageId: string
): { stage: WorkflowStageOutDto; workflow: IssueWorkflowOutDto } | undefined {
  for (const workflow of workflows) {
    const stage = workflow.stages?.find((candidate) =>
      areWorkflowEntityIdsEqual(candidate._id, stageId)
    );
    if (stage) {
      return { stage, workflow };
    }
  }
  return undefined;
}

export function findStagesByWorkflowId(
  workflows: IssueWorkflowOutDto[],
  workflowId: string
): WorkflowStageOutDto[] {
  const workflow = findWorkflowById(workflows, workflowId);
  if (!workflow) return [];
  return [...(workflow.stages ?? [])]
    .filter((stage) => !stage.deleted)
    .sort(sortByFractionalOrder);
}

export function findStageIndex(
  workflows: IssueWorkflowOutDto[],
  stageId: string,
  workflowId: string
): number {
  const stages = findStagesByWorkflowId(workflows, workflowId);
  return stages.findIndex((stage) =>
    areWorkflowEntityIdsEqual(stage._id, stageId)
  );
}

export function findStageFromWorkflow(
  workflows: IssueWorkflowOutDto[],
  workflowId: string,
  stageId: string
): WorkflowStageOutDto | undefined {
  const workflow = findWorkflowById(workflows, workflowId);
  return workflow?.stages.find((stage) =>
    areWorkflowEntityIdsEqual(stage._id, stageId)
  );
}

/**
 * The workflow the server saved, merged into the one the store knows. An empty
 * answer keeps the known workflow; one without active stages keeps the known
 * stages (some changes answer with a partial workflow).
 */
export function mergeSavedWorkflow(
  known: IssueWorkflowOutDto | undefined,
  saved: IssueWorkflowOutDto
): IssueWorkflowOutDto {
  if (!saved._id || !saved.name) {
    return known ?? saved;
  }
  const activeStages = (saved.stages ?? []).filter((stage) => !stage.deleted);
  const knownStages = known?.stages ?? [];
  if (known && activeStages.length === 0 && knownStages.length > 0) {
    return { ...known, ...saved, stages: [...knownStages] };
  }
  return { ...saved, stages: activeStages };
}

export function addWorkflow(
  workflows: IssueWorkflowOutDto[],
  created: IssueWorkflowOutDto
): IssueWorkflowOutDto[] {
  return [...workflows, created];
}

export function replaceWorkflow(
  workflows: IssueWorkflowOutDto[],
  workflowId: string,
  saved: IssueWorkflowOutDto
): IssueWorkflowOutDto[] {
  return workflows.map((workflow) =>
    areWorkflowEntityIdsEqual(workflow._id, workflowId)
      ? mergeSavedWorkflow(workflow, saved)
      : workflow
  );
}

function updateStagesOfWorkflow(
  workflows: IssueWorkflowOutDto[],
  workflowId: string,
  update: (stages: WorkflowStageOutDto[]) => WorkflowStageOutDto[]
): IssueWorkflowOutDto[] {
  return workflows.map((workflow) =>
    areWorkflowEntityIdsEqual(workflow._id, workflowId)
      ? { ...workflow, stages: update(workflow.stages ?? []) }
      : workflow
  );
}

/** Adds the stage to its workflow (replacing a stage with the same id). */
export function upsertStage(
  workflows: IssueWorkflowOutDto[],
  workflowId: string,
  stage: WorkflowStageOutDto
): IssueWorkflowOutDto[] {
  return updateStagesOfWorkflow(workflows, workflowId, (stages) => [
    ...stages.filter((known) => !areWorkflowEntityIdsEqual(known._id, stage._id)),
    stage,
  ]);
}

export function replaceStage(
  workflows: IssueWorkflowOutDto[],
  workflowId: string,
  stageId: string,
  saved: WorkflowStageOutDto
): IssueWorkflowOutDto[] {
  return updateStagesOfWorkflow(workflows, workflowId, (stages) =>
    stages.map((stage) => (areWorkflowEntityIdsEqual(stage._id, stageId) ? saved : stage))
  );
}

export function removeStage(
  workflows: IssueWorkflowOutDto[],
  workflowId: string,
  stageId: string
): IssueWorkflowOutDto[] {
  return updateStagesOfWorkflow(workflows, workflowId, (stages) =>
    stages.filter((stage) => !areWorkflowEntityIdsEqual(stage._id, stageId))
  );
}
